import { RefObject, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { AddressData } from '@/shared/types/AddressData';
import { useErrorHandler } from '@/shared/hooks/useErrorHandler';

import { useAlertContext } from '@/contexts/alert.context';
import { providerCompanyService } from '@/services/providerCompany.service';
import { AddressFormRef } from '@/components/AddressForm';

export type ProviderRegisterFormData = AddressData & {
  cnpj: string;
  name: string;
  email: string;
  phone: string;
  minValue: string;
  maxValue: string;
  phoneType: string;
  shortName: string;
  propertyTypes: string[];
  integrationCode: string;
  inspectionTypes: string[];
  allowsDemandReceiving: boolean;
};

interface UseProviderRegisterProps {
  addressFormRef: RefObject<AddressFormRef>;
}

export function useProviderRegister({ addressFormRef }: UseProviderRegisterProps) {
  const { handleError } = useErrorHandler();

  const navigation = useNavigate();

  const { notify } = useAlertContext();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const onSubmit = async (data: ProviderRegisterFormData) => {
    if (!addressFormRef.current?.isCepValidated()) {
      notify({ message: 'CEP não encontrado. Informe um CEP válido.', type: 'error' });
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        city: data.city,
        allowsDemandReceiving: data.allowsDemandReceiving ?? false,
        cnpj: data.cnpj,
        email: data.email,
        minValue: Number(data.minValue?.replace(/\D/g, '')) || 0,
        maxValue: Number(data.maxValue?.replace(/\D/g, '')) || 0,
        propertyTypeIds: data.propertyTypes || [],
        inspectionTypeIds: data.inspectionTypes || [],
        district: data.district,
        integrationCode: data.integrationCode || '',
        name: data.name,
        number: data.number ?? '',
        phones: [{ phoneNumber: data.phone, phoneType: data.phoneType }],
        postalCode: data.postalCode.replace(/\./g, '').replace(/-/g, ''),
        shortName: data.shortName,
        state: data.state,
        street: data.street,
        complement: data.complement ?? '',
      };

      await providerCompanyService.createProvider(payload);

      notify({ message: 'Sucesso ao criar a Prestadora.', type: 'success' });

      navigation('/vendor-list');
    } catch (error) {
      handleError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    onSubmit,
    isSubmitting,
  };
}
